import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ScenarioDataService from "../services/ScenarioService";
import { debugLog, errorLog } from "../utils/debugUtils";
import { getRejectedError, toRejectedPayload } from "../utils/sliceErrorUtils";

// Exported so tests and preloaded stores can reuse the full slice shape,
// including the structured error/status objects expected by the UI.
export const initialState = {
	list: [],
	selectedScenario: null,
	status: {
		retrieve: "idle",
		retrieveOne: "idle",
		create: "idle",
		update: "idle",
		delete: "idle",
		import: "idle",
		copy: "idle",
	},
	error: {
		retrieve: null,
		retrieveOne: null,
		create: null,
		update: null,
		delete: null,
		import: null,
		copy: null,
	},
};

export const createScenario = createAsyncThunk(
	"scenarios/create",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.create(data);
			debugLog(3, "[scenarioSlice][FLOW] createScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] createScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario create failed."));
		}
	}
);

export const retrieveScenarios = createAsyncThunk(
	"scenarios/retrieve",
	async (_, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.getAll();
			debugLog(3, "[scenarioSlice][FLOW] retrieveScenarios response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] retrieveScenarios rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario retrieve failed."));
		}
	}
);

export const retrieveOneScenario = createAsyncThunk(
	"scenarios/retrieveOne",
	async (id, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.get(id);
			debugLog(3, "[scenarioSlice][FLOW] retrieveOneScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] retrieveOneScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario retrieve failed."));
		}
	}
);

export const retrieveOneScenarioByUuid = createAsyncThunk(
	"scenarios/retrieveOneByUuid",
	async (uuid, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.findByUuid(uuid);
			debugLog(3, "[scenarioSlice][FLOW] retrieveOneScenarioByUuid response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] retrieveOneScenarioByUuid rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario retrieve failed."));
		}
	}
);

export const dumpOneScenario = createAsyncThunk(
	"scenarios/dumpOne",
	async (id, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.dump(id);
			debugLog(3, "[scenarioSlice][FLOW] dumpOneScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] dumpOneScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario dump failed."));
		}
	}
);

export const updateScenario = createAsyncThunk(
	"scenarios/update",
	async ({ id, data }, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.update(id, data);
			debugLog(3, "[scenarioSlice][FLOW] updateScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] updateScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario update failed."));
		}
	}
);

export const importScenario = createAsyncThunk(
	"scenarios/import",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.importScenario(data);
			debugLog(3, "[scenarioSlice][FLOW] importScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] importScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario import failed."));
		}
	}
);

export const copyScenario = createAsyncThunk(
	"scenarios/copy",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.copy(data);
			debugLog(3, "[scenarioSlice][FLOW] copyScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] copyScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario copy failed."));
		}
	}
);

export const deleteScenario = createAsyncThunk(
	"scenarios/deleteScenario",
	async ({ data }, { rejectWithValue }) => {
		try {
			const res = await ScenarioDataService.remove(data);
			debugLog(3, "[scenarioSlice][FLOW] deleteScenario response", { response: res.data });
			return res.data;
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] deleteScenario rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario delete failed."));
		}
	}
);

export const updateScenarioStatus = createAsyncThunk(
	"scenarios/updateStatus",
	async ({ id, status_id, updatedBy }, { rejectWithValue }) => {
		try {
			const response = await ScenarioDataService.updateScenarioStatus(id, status_id, updatedBy);
			debugLog(3, "[scenarioSlice][FLOW] updateScenarioStatus response", { response: response.data });
			return { id, status_id }; // Minimal return; adjust as needed
		} catch (err) {
			errorLog(4, "[scenarioSlice][PROCESS] updateScenarioStatus rejected", { error: err });
			return rejectWithValue(toRejectedPayload(err, "Scenario status update failed."));
		}
	}
);

const scenarioSlice = createSlice({
	name: "scenarios",
	initialState,
	reducers: {
		setSelectedScenario: (state, action) => {
			state.selectedScenario = action.payload ?? null;
			debugLog(4, "[scenarioSlice][PROCESS] setSelectedScenario", { payload: action.payload });
		},
		clearSelectedScenario: (state) => {
			state.selectedScenario = null;
			debugLog(4, "[scenarioSlice][PROCESS] clearSelectedScenario", {});
		},
		resetError: (state, action) => {
			if (action.payload && action.payload.type) {
				state.error[action.payload.type] = null;
				debugLog(4, "[scenarioSlice][PROCESS] resetError one", { type: action.payload.type });
			} else {
				Object.keys(state.error).forEach((key) => {
					state.error[key] = null;
				});
				debugLog(4, "[scenarioSlice][PROCESS] resetError all", {});
			}
		},
	},
	extraReducers: (builder) => {
		builder

			// Retrieve all
			.addCase(retrieveScenarios.pending, (state) => {
				state.status.retrieve = "loading";
				state.error.retrieve = null;
			})
			.addCase(retrieveScenarios.fulfilled, (state, action) => {
				state.status.retrieve = "succeeded";
				state.list = action.payload;
			})
			.addCase(retrieveScenarios.rejected, (state, action) => {
				state.status.retrieve = "failed";
				// Preserve the normalized error object so Scenario list/detail pages
				// can show backend messages and transport fallbacks consistently.
				state.error.retrieve = getRejectedError(action, "Scenario retrieve failed.");
				debugLog(4, "[scenarioSlice][PROCESS] retrieveScenarios rejected", { error: action.error });
			})

			// Retrieve one
			.addCase(retrieveOneScenario.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneScenario.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedScenario = action.payload;
			})
			.addCase(retrieveOneScenario.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Scenario retrieve failed.");
				debugLog(4, "[scenarioSlice][PROCESS] retrieveOneScenario rejected", { error: action.error });
			})

			// Retrieve one by uuid
			.addCase(retrieveOneScenarioByUuid.pending, (state) => {
				state.status.retrieveOne = "loading";
				state.error.retrieveOne = null;
			})
			.addCase(retrieveOneScenarioByUuid.fulfilled, (state, action) => {
				state.status.retrieveOne = "succeeded";
				state.selectedScenario = action.payload;
			})
			.addCase(retrieveOneScenarioByUuid.rejected, (state, action) => {
				state.status.retrieveOne = "failed";
				state.error.retrieveOne = getRejectedError(action, "Scenario retrieve failed.");
				debugLog(4, "[scenarioSlice][PROCESS] retrieveOneScenarioByUuid rejected", {
					error: action.error,
				});
			})

			// Create
			.addCase(createScenario.pending, (state) => {
				state.status.create = "loading";
				state.error.create = null;
			})
			.addCase(createScenario.fulfilled, (state, action) => {
				state.status.create = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(createScenario.rejected, (state, action) => {
				state.status.create = "failed";
				// Preserve the normalized mutation error so Scenario create/update
				// flows can surface backend messages through the shared dialog contract.
				state.error.create = getRejectedError(action, "Scenario create failed.");
				debugLog(4, "[scenarioSlice][PROCESS] createScenario rejected", { error: action.error });
			})

			// Update
			.addCase(updateScenario.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updateScenario.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				const idx = state.list.findIndex((x) => x.id === action.payload.id);
				if (idx !== -1) state.list[idx] = action.payload;
				// Optionally, update selectedScenario
				if (state.selectedScenario && state.selectedScenario.id === action.payload.id) {
					state.selectedScenario = action.payload;
				}

				debugLog(4, "[scenarioSlice][PROCESS] updateScenario fulfilled updated scenario", {
					payload: action.payload,
				});
			})
			.addCase(updateScenario.rejected, (state, action) => {
				state.status.update = "failed";
				// Keep the shared error shape here so update dialogs can preserve
				// backend messages instead of falling back to module-specific text.
				state.error.update = getRejectedError(action, "Scenario update failed.");
				debugLog(4, "[scenarioSlice][PROCESS] updateScenario rejected", { error: action.error });
			})

			// Delete
			.addCase(deleteScenario.pending, (state) => {
				state.status.delete = "loading";
				state.error.delete = null;
			})
			.addCase(deleteScenario.fulfilled, (state, action) => {
				state.status.delete = "succeeded";
				const deletedId =
					typeof action.payload === "number"
						? action.payload
						: action.payload?.id ?? action.meta?.arg?.data?.id;
				if (deletedId == null) return;

				state.list = state.list.filter((x) => x.id !== deletedId);
				if (state.selectedScenario && state.selectedScenario.id === deletedId) {
					state.selectedScenario = null;
				}
			})
			.addCase(deleteScenario.rejected, (state, action) => {
				state.status.delete = "failed";
				// Keep delete failures structured so dialogs can preserve backend
				// messages instead of falling back to generic module text.
				state.error.delete = getRejectedError(action, "Scenario delete failed.");
				debugLog(4, "[scenarioSlice][PROCESS] deleteScenario rejected", { error: action.error });
			})

			// Import
			.addCase(importScenario.pending, (state) => {
				state.status.import = "loading";
				state.error.import = null;
			})
			.addCase(importScenario.fulfilled, (state, action) => {
				state.status.import = "succeeded";
				state.list.push(action.payload);
			})
			.addCase(importScenario.rejected, (state, action) => {
				state.status.import = "failed";
				// Keep the shared error shape here so the two-stage Scenario import
				// flow can surface the original backend message without custom parsing.
				state.error.import = getRejectedError(action, "Scenario import failed.");
				debugLog(4, "[scenarioSlice][PROCESS] importScenario rejected", { error: action.error });
			})

			// Copy
			.addCase(copyScenario.pending, (state) => {
				state.status.copy = "loading";
				state.error.copy = null;
			})
			.addCase(copyScenario.fulfilled, (state, action) => {
				state.status.copy = "succeeded";
				const copiedScenario =
					action.payload && typeof action.payload === "object" && action.payload.id != null
						? action.payload
						: action.payload?.scenario && action.payload.scenario.id != null
							? action.payload.scenario
							: null;

				if (copiedScenario) {
					state.list.push(copiedScenario);
				}
			})
			.addCase(copyScenario.rejected, (state, action) => {
				state.status.copy = "failed";
				state.error.copy = getRejectedError(action, "Scenario copy failed.");
				debugLog(4, "[scenarioSlice][PROCESS] copyScenario rejected", { error: action.error });
			})

			// Status update
			.addCase(updateScenarioStatus.pending, (state) => {
				state.status.update = "loading";
				state.error.update = null;
			})
			.addCase(updateScenarioStatus.fulfilled, (state, action) => {
				state.status.update = "succeeded";
				// Merge the returned status change into any existing record so we do
				// not drop the rest of the Scenario fields on a successful archive/restore.
				const idx = state.list.findIndex((x) => x.id === action.payload.id);
				if (idx !== -1) {
					state.list[idx] = {
						...state.list[idx],
						...action.payload,
					};
				}
				if (state.selectedScenario && state.selectedScenario.id === action.payload.id) {
					state.selectedScenario = {
						...state.selectedScenario,
						...action.payload,
					};
				}
			})
			.addCase(updateScenarioStatus.rejected, (state, action) => {
				state.status.update = "failed";
				// Status updates still reuse the shared `update` error bucket, so
				// keep the normalized object for consistent dialog rendering.
				state.error.update = getRejectedError(action, "Scenario status update failed.");
				debugLog(4, "[scenarioSlice][PROCESS] updateScenarioStatus rejected", {
					error: action.error,
				});
			});
	},
});

const { reducer } = scenarioSlice;
export default reducer;

// Export action creators for UI
export const { setSelectedScenario, clearSelectedScenario, resetError } = scenarioSlice.actions;

// [OPTIONAL] Export selectors for consistent access
export const selectScenarioList = (state) => state.scenarios.list;
export const selectScenarioStatus = (state) => state.scenarios.status;
export const selectScenarioError = (state) => state.scenarios.error;
export const selectSelectedScenario = (state) => state.scenarios.selectedScenario;
